import type { GridColDef } from '@mui/x-data-grid';
import type { FieldDef } from '@/components/RecordDialog';

export interface MasterConfig {
  doctype: string;
  endpoint: string;
  titleKey: string;
  columns: GridColDef[];
  fields: FieldDef[];
}

const nameColumns = (extra: GridColDef[] = []): GridColDef[] => [
  { field: 'name', headerName: 'col.name', flex: 1, minWidth: 180 },
  { field: 'nameAr', headerName: 'col.name_ar', flex: 1, minWidth: 180 },
  ...extra,
  { field: 'isActive', headerName: 'col.active', type: 'boolean', width: 100 },
];

const nameFields = (extra: FieldDef[] = []): FieldDef[] => [
  { name: 'name', label: 'Name', required: true },
  { name: 'nameAr', label: 'Arabic name' },
  ...extra,
  { name: 'isActive', label: 'Active', type: 'switch' },
];

/**
 * Screen definitions for masters with no bespoke behaviour. Adding a master to
 * the UI is a config entry plus a route, matching the generated controllers on
 * the API side.
 */
export const MASTER_CONFIGS: Record<string, MasterConfig> = {
  designations: {
    doctype: 'Designation',
    endpoint: '/designations',
    titleKey: 'nav.designations',
    columns: nameColumns([{ field: 'description', headerName: 'col.description', flex: 1 }]),
    fields: nameFields([{ name: 'description', label: 'Description', type: 'multiline' }]),
  },
  'employee-grades': {
    doctype: 'Employee Grade',
    endpoint: '/employee-grades',
    titleKey: 'nav.grades',
    columns: nameColumns([
      { field: 'minSalary', headerName: 'col.min_salary', type: 'number', width: 130 },
      { field: 'maxSalary', headerName: 'col.max_salary', type: 'number', width: 130 },
    ]),
    fields: nameFields([
      { name: 'minSalary', label: 'Minimum salary', type: 'number' },
      { name: 'maxSalary', label: 'Maximum salary', type: 'number' },
      // What a new hire on this grade inherits. Without them the grade is a
      // label, and every contract and entitlement is created by hand.
      {
        name: 'defaultSalaryStructureId',
        label: 'Default salary structure',
        type: 'link',
        doctype: 'Salary Structure',
        helperText: 'Drafts a salary contract at the grade minimum when someone is hired onto it',
      },
      {
        name: 'defaultLeavePolicyId',
        label: 'Default leave policy',
        type: 'link',
        doctype: 'Leave Policy',
        helperText: 'Drafts the leave entitlement when someone is hired onto it',
      },
    ]),
  },
  'employment-types': {
    doctype: 'Employment Type',
    endpoint: '/employment-types',
    titleKey: 'nav.employment_types',
    columns: nameColumns([
      { field: 'isFixedTerm', headerName: 'col.fixed_term', type: 'boolean', width: 120 },
    ]),
    fields: nameFields([
      {
        name: 'isFixedTerm',
        label: 'Fixed-term contract',
        type: 'switch',
        helperText: 'Drives contract-end alerts under Labour Law 14/2025',
      },
    ]),
  },
  administrations: {
    doctype: 'Administration',
    endpoint: '/administrations',
    titleKey: 'nav.administrations',
    columns: nameColumns(),
    fields: nameFields(),
  },
  nationalities: {
    doctype: 'Nationality',
    endpoint: '/nationalities',
    titleKey: 'nav.nationalities',
    columns: nameColumns([
      { field: 'isoCode', headerName: 'col.iso', width: 80 },
      { field: 'isLocal', headerName: 'col.egyptian', type: 'boolean', width: 110 },
      { field: 'requiresWorkPermit', headerName: 'col.work_permit', type: 'boolean', width: 130 },
    ]),
    fields: nameFields([
      { name: 'isoCode', label: 'ISO code' },
      { name: 'isLocal', label: 'Local (Egyptian)', type: 'switch' },
      {
        name: 'requiresWorkPermit',
        label: 'Requires work permit',
        type: 'switch',
        helperText: 'Feeds the foreign-worker quota and permit expiry alerts',
      },
    ]),
  },
};
